import {useMemo, useCallback, useState, useEffect, useRef} from "react";
import {DataTable, DataTableColumn} from "mantine-datatable";
import {Badge, Flex, Stack, Text, Tooltip, useMantineTheme, Box} from "@mantine/core";
import {MaterialSymbol} from "react-material-symbols";
import {ArticleMarketplaceStatus, MarketplaceName} from "../../../interfaces/response/MarketplaceStatus";
import {FilterValues} from "./MarketplaceFilters";
import classes from "./MarketplaceStatusTable.module.css";

/**
 * Props per il componente MarketplaceStatusTable
 */
interface MarketplaceStatusTableProps {
    /** Dati degli articoli con stato marketplace */
    data?: ArticleMarketplaceStatus[];
    /** Indica se i dati sono in caricamento */
    loading: boolean;
    /** Filtri applicati alla tabella */
    filters: FilterValues;
}

/**
 * Componente tabella per visualizzare lo stato degli articoli sui marketplace.
 * Mostra per ogni articolo:
 * - Immagine e dettagli prodotto
 * - Stato su ogni marketplace (abilitato, in feed, pubblicato, vendibile)
 * - Warning e messaggi di errore
 * 
 * La tabella supporta filtri per marketplace, stato e ricerca testuale.
 */
const MarketplaceStatusTable = ({data, loading, filters}: MarketplaceStatusTableProps) => {
    const theme = useMantineTheme();
    
    // Paginazione lazy per performance con grandi dataset
    const PAGE_SIZE = 50;
    const [displayedRecordsCount, setDisplayedRecordsCount] = useState(PAGE_SIZE);
    const [rowLoading, setRowLoading] = useState(false);
    
    // Ref per il sentinella di fine pagina (infinite scroll)
    const bottomSentinelRef = useRef<HTMLDivElement>(null);

    // Filtra i dati in base ai filtri applicati
    // Logica: filtri diversi in AND, checkbox dello stesso filtro in OR
    // Ottimizzato con dipendenze granulari per evitare re-render inutili
    const filteredData = useMemo(() => {
        if (!data) return [];

        // Pre-calcola i filtri per evitare accessi ripetuti
        const hasSearchFilter = !!filters.search;
        const searchLower = filters.search?.toLowerCase() || '';
        const hasMarketplaceFilter = filters.marketplaces.length > 0;
        const hasCategoryFilter = filters.categories.length > 0;
        const hasStatusFilter = filters.statuses.length > 0;
        const hasGradeFilter = filters.grades.length > 0;
        const hasAvailabilityFilter = filters.availability !== undefined;

        return data.filter(article => {
            // Early exit: filtro ricerca testuale (AND)
            if (hasSearchFilter) {
                if (!article.sku.toLowerCase().includes(searchLower) &&
                    !article.ean.toLowerCase().includes(searchLower) &&
                    !article.title.toLowerCase().includes(searchLower) &&
                    !article.category.toLowerCase().includes(searchLower)) {
                    return false;
                }
            }

            // Early exit: filtro categorie (OR)
            if (hasCategoryFilter && !filters.categories.includes(article.category)) {
                return false;
            }

            // Early exit: filtro grado (OR)
            if (hasGradeFilter && (!article.grade || !filters.grades.includes(article.grade))) {
                return false;
            }

            // Early exit: filtro disponibilità
            if (hasAvailabilityFilter && article.availability !== filters.availability) {
                return false;
            }

            // Filtro marketplace e stato combinati
            // Gli stati si applicano SOLO ai marketplace selezionati (o tutti se nessun marketplace selezionato)
            const targetMarketplaces = hasMarketplaceFilter ? filters.marketplaces : (['EBAY', 'MEDIAWORLD', 'LEROY_MERLIN', 'KELKOO', 'EPRICE'] as MarketplaceName[]);
            
            // Se non showDisabled, escludi articoli che non hanno NESSUN marketplace abilitato tra quelli target
            if (!filters.showDisabled) {
                const hasAnyEnabled = targetMarketplaces.some(marketplace => 
                    article.marketplaceStatuses[marketplace]?.enabledInBackoffice
                );
                if (!hasAnyEnabled) return false;
            }

            // Filtro Avvertimenti
            // Se warnings è vuoto (nessuna checkbox selezionata), mostra tutti.
            // Se ci sono selezioni, mostra articoli che hanno ALMENO UNO dei warning selezionati
            if (filters.warnings.length > 0) {
                const articleWarnings = new Set<string>();
                targetMarketplaces.forEach(marketplace => {
                    const warnings = article.marketplaceStatuses[marketplace]?.publicationStatus?.warnings;
                    warnings?.forEach(w => articleWarnings.add(w));
                });

                // Mostra solo articoli che hanno almeno uno dei warning selezionati
                const hasMatchingWarning = filters.warnings.some(selectedWarning => 
                    articleWarnings.has(selectedWarning)
                );
                if (!hasMatchingWarning) return false;
            }

            // Filtro stato (OR tra stati selezionati) - applicato solo ai marketplace target
            if (hasStatusFilter) {
                const hasStatus = targetMarketplaces.some(marketplace => {
                    const status = article.marketplaceStatuses[marketplace];
                    if (!status) return false;
                    
                    // Se showDisabled è false, considera solo quelli abilitati
                    if (!filters.showDisabled && !status.enabledInBackoffice) return false;

                    return filters.statuses.some(filterStatus => {
                        const feedSupported = status.feedStatus?.available;
                        const pubSupported = status.publicationStatus?.available;

                        switch (filterStatus) {
                            case 'enabled_not_in_feed':
                                // Mostra solo se il feed è supportato e manca
                                return feedSupported && !status.feedStatus?.present;
                            
                            case 'in_feed_not_published':
                                // Se pubblicazione non supportata, questo errore non ha senso
                                if (!pubSupported) return false;
                                // Se feed supportato: deve essere nel feed E non pubblicato
                                // Se feed NON supportato: deve essere abilitato E non pubblicato
                                const preRequisiteMet = feedSupported 
                                    ? status.feedStatus?.present 
                                    : status.enabledInBackoffice;
                                return preRequisiteMet && !status.publicationStatus?.published;
                            
                            case 'published_not_sellable':
                                // Solo se pubblicazione supportata
                                return pubSupported && status.publicationStatus?.published && !status.publicationStatus?.sellable;
                            
                            case 'enabled_not_sellable':
                                // Abilitato ma non vendibile (solo se pubblicazione supportata, altrimenti non ha concetto di vendibile)
                                return pubSupported && status.enabledInBackoffice && !status.publicationStatus?.sellable;
                            
                            case 'in_feed_but_suspended':
                                // Solo per Mirakl: articolo nel feed ma sospeso
                                if (!status.publicationStatus?.suspendedDetectionAvailable) return false;
                                const inFeedForSuspended = feedSupported ? status.feedStatus?.present : status.enabledInBackoffice;
                                return inFeedForSuspended && status.publicationStatus?.suspended;
                            
                            case 'in_feed_but_invalid_data':
                                // Solo per Mirakl: articolo nel feed ma con dati invalidi
                                if (!status.publicationStatus?.suspendedDetectionAvailable) return false;
                                const inFeedForInvalid = feedSupported ? status.feedStatus?.present : status.enabledInBackoffice;
                                return inFeedForInvalid && status.publicationStatus?.invalidData;
                            
                            case 'fully_operational':
                                // Considera OK se la feature è presente OPPURE se non è supportata
                                const isFeedOk = !feedSupported || status.feedStatus?.present;
                                const isPubOk = !pubSupported || (status.publicationStatus?.published && status.publicationStatus?.sellable);
                                // Per Mirakl, verifica anche che non sia sospeso o con dati invalidi
                                const isMiraklOk = !status.publicationStatus?.suspendedDetectionAvailable || 
                                    (!status.publicationStatus?.suspended && !status.publicationStatus?.invalidData);
                                return isFeedOk && isPubOk && isMiraklOk;
                                
                            default:
                                return false;
                        }
                    });
                });
                if (!hasStatus) return false;
            }

            return true;
        });
    }, [
        data, 
        filters.search, 
        filters.marketplaces, 
        filters.categories, 
        filters.statuses, 
        filters.grades, 
        filters.availability,
        filters.showDisabled,
        filters.warnings
    ]);
    
    // Reset displayed count quando cambiano i filtri
    useEffect(() => {
        setDisplayedRecordsCount(PAGE_SIZE);
    }, [filters]);
    
    // Dati effettivamente visualizzati (limitati per performance)
    const displayedRecords = useMemo(() => {
        return filteredData.slice(0, displayedRecordsCount);
    }, [filteredData, displayedRecordsCount]);
    
    // Carica più record quando si scrolla verso il basso (Infinite Scroll)
    const loadMoreRecords = useCallback(() => {
        if (!rowLoading && displayedRecordsCount < filteredData.length) {
            setRowLoading(true);
            setTimeout(() => {
                setDisplayedRecordsCount(prev => Math.min(prev + PAGE_SIZE, filteredData.length));
                setRowLoading(false);
            }, 300);
        }
    }, [displayedRecordsCount, filteredData.length, rowLoading]);

    // Observer per triggerare il caricamento quando si arriva in fondo
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                loadMoreRecords();
            }
        }, {
            root: null, // viewport del browser
            rootMargin: '100px', // carica un po' prima di arrivare in fondo
            threshold: 0.1
        });

        if (bottomSentinelRef.current) {
            observer.observe(bottomSentinelRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [loadMoreRecords]);

    const successColor = theme.colors.green[6];
    const errorColor = theme.colors.red[6];

    const renderMarketplaceColumn = useCallback((marketplace: MarketplaceName) => {
        return (article: ArticleMarketplaceStatus) => {
            const status = article.marketplaceStatuses[marketplace];
            
            // Se NON mostriamo i disabilitati e questo è disabilitato, mostra badge
            if (!status?.enabledInBackoffice && !filters.showDisabled) {
                return (
                    <Badge color="gray" size="sm" variant="light" className={classes.statusBadge}>
                        Disabilitato
                    </Badge>
                );
            }

            // Colori accessibili WCAG AA compliant
            const successColor = '#2f7c31'; // Verde scuro accessibile (contrast ratio > 4.5:1)
            const errorColor = '#c41c1c';   // Rosso scuro accessibile (contrast ratio > 4.5:1)
            
            // Se showDisabled è TRUE, mostra sempre le icone (con prima icona rossa se disabilitato)
            const isEnabled = status?.enabledInBackoffice;

            return (
                <Stack gap={6}>
                    <Flex gap={8} align="center">
                        {/* Prima icona: verde se abilitato, rossa se disabilitato */}
                        <Tooltip 
                            label={isEnabled ? "Abilitato in backoffice" : "Disabilitato in backoffice"} 
                            withArrow
                        >
                            <Box className={classes.iconWrapper}>
                                <MaterialSymbol 
                                    icon="check_circle" 
                                    size={18} 
                                    color={isEnabled ? successColor : errorColor}
                                />
                            </Box>
                        </Tooltip>

                        <Tooltip 
                            label={
                                !status.feedStatus?.available 
                                    ? "Feed non disponibile per questo marketplace"
                                    : status.feedStatus?.present 
                                        ? `Presente nel feed (${new Date(status.feedStatus.lastUpdate || '').toLocaleString('it-IT')})` 
                                        : "Non presente nel feed"
                            }
                            withArrow
                        >
                            <Box className={classes.iconWrapper}>
                                <MaterialSymbol
                                    icon="rss_feed"
                                    size={18}
                                    color={
                                        !status.feedStatus?.available 
                                            ? theme.colors.gray[5]
                                            : status.feedStatus?.present 
                                                ? successColor 
                                                : errorColor
                                    }
                                />
                            </Box>
                        </Tooltip>

                        <Tooltip
                            label={
                                !status.publicationStatus?.available
                                    ? "Pubblicazione non disponibile per questo marketplace"
                                    : status.publicationStatus?.published 
                                        ? "Pubblicato" 
                                        : "Non pubblicato"
                            }
                            withArrow
                        >
                            <Box className={classes.iconWrapper}>
                                <MaterialSymbol
                                    icon="cloud_done"
                                    size={18}
                                    color={
                                        !status.publicationStatus?.available
                                            ? theme.colors.gray[5]
                                            : status.publicationStatus?.published 
                                                ? successColor 
                                                : errorColor
                                    }
                                />
                            </Box>
                        </Tooltip>

                        <Tooltip
                            label={
                                !status.publicationStatus?.available
                                    ? "Pubblicazione non disponibile per questo marketplace"
                                    : status.publicationStatus?.sellable 
                                        ? "Vendibile" 
                                        : "Non vendibile"
                            }
                            withArrow
                        >
                            <Box className={classes.iconWrapper}>
                                <MaterialSymbol
                                    icon="shopping_cart"
                                    size={18}
                                    color={
                                        !status.publicationStatus?.available
                                            ? theme.colors.gray[5]
                                            : status.publicationStatus?.sellable 
                                                ? successColor 
                                                : errorColor
                                    }
                                />
                            </Box>
                        </Tooltip>

                        {/* Warning Icon if validation warnings exist (orange) */}
                        {status.publicationStatus?.warnings && status.publicationStatus.warnings.length > 0 && (
                            <Tooltip 
                                label={
                                    <Stack gap={4}>
                                        <Text size="xs" fw={600}>Avvertimenti dati:</Text>
                                        {status.publicationStatus.warnings.map((w, i) => (
                                            <Text key={i} size="xs">{w}</Text>
                                        ))}
                                    </Stack>
                                } 
                                multiline 
                                withArrow
                            >
                                <Box className={classes.iconWrapper}>
                                    <MaterialSymbol
                                        icon="warning"
                                        size={18}
                                        color={theme.colors.orange[6]}
                                    />
                                </Box>
                            </Tooltip>
                        )}

                        {/* Marketplace Warning Icon if marketplace warnings exist (red triangle) */}
                        {status.publicationStatus?.marketplaceWarnings && status.publicationStatus.marketplaceWarnings.length > 0 && (
                            <Tooltip 
                                label={
                                    <Stack gap={4}>
                                        <Text size="xs" fw={600}>Warning dal marketplace:</Text>
                                        {status.publicationStatus.marketplaceWarnings.map((w, i) => (
                                            <Text key={i} size="xs">{w}</Text>
                                        ))}
                                    </Stack>
                                } 
                                multiline 
                                withArrow
                            >
                                <Box className={classes.iconWrapper}>
                                    <MaterialSymbol
                                        icon="report"
                                        size={18}
                                        color={theme.colors.red[7]}
                                    />
                                </Box>
                            </Tooltip>
                        )}
                    </Flex>
                </Stack>
            );
        };
    }, [theme, filters.showDisabled]);

    const columns: DataTableColumn<ArticleMarketplaceStatus>[] = [
        {
            accessor: 'image',
            title: '',
            width: 70,
            cellsClassName: classes.imageCell,
            render: (article) => (
                <img
                    src={article.imageUrl}
                    alt={article.title}
                    className={classes.productImage}
                />
            )
        },
        {
            accessor: 'sku',
            title: 'SKU',
            width: 120,
            sortable: true,
            render: (article) => (
                <Text size="sm" fw={500}>{article.sku}</Text>
            )
        },
        {
            accessor: 'title',
            title: 'Prodotto',
            width: '25%',
            render: (article) => (
                <Stack gap={4}>
                    <Text size="sm" fw={500} className={classes.titleText}>
                        {article.title}
                    </Text>
                    <Text size="xs" c="dimmed">EAN: {article.ean}</Text>
                    <Text size="xs" c="dimmed">Categoria: {article.category}</Text>
                    <Flex gap={4} mt={4}>
                        {article.grade && (
                            <Badge size="xs" color="blue" variant="light">
                                Grado: {article.grade}
                            </Badge>
                        )}
                        {article.availability !== undefined && (
                            <Badge 
                                size="xs" 
                                color={article.availability ? "teal.7" : "red"} 
                                variant="light"
                                style={article.availability ? {backgroundColor: '#0d5e52', color: 'white'} : undefined}
                            >
                                {article.availability ? "Disponibile" : "Non disponibile"}
                            </Badge>
                        )}
                    </Flex>
                </Stack>
            )
        },
        {
            accessor: 'ebay',
            title: 'eBay',
            width: 150,
            render: renderMarketplaceColumn('EBAY')
        },
        {
            accessor: 'mediaworld',
            title: 'Mediaworld',
            width: 150,
            render: renderMarketplaceColumn('MEDIAWORLD')
        },
        {
            accessor: 'leroymerlin',
            title: 'Leroy Merlin',
            width: 150,
            render: renderMarketplaceColumn('LEROY_MERLIN')
        },
        {
            accessor: 'kelkoo',
            title: 'Kelkoo',
            width: 150,
            render: renderMarketplaceColumn('KELKOO')
        },
        {
            accessor: 'eprice',
            title: 'ePrice',
            width: 150,
            render: renderMarketplaceColumn('EPRICE')
        }
    ];

    return (
        <>
            <Flex justify="flex-end" mb="xs">
                <Text size="xs">Risultati Trovati: {filteredData.length}</Text>
            </Flex>
            
            <div className={classes.tableWrapper}>
                <DataTable
                    columns={columns}
                    records={displayedRecords}
                    fetching={loading || rowLoading}
                    noRecordsText="Nessun articolo trovato"
                    highlightOnHover
                    classNames={{
                        table: classes.table,
                        header: classes.tableHeader
                    }}
                    rowClassName={classes.tableRow}
                    // Virtualizzazione per migliori performance con molti record
                    loaderSize="sm"
                    loaderType="dots"
                    loaderBackgroundBlur={2}
                />
                
                {/* Elemento sentinella per infinite scroll */}
                <div ref={bottomSentinelRef} style={{ height: '20px', width: '100%' }} />

                {displayedRecords.length < filteredData.length && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '10px', 
                        color: theme.colors.gray[6],
                        borderTop: `1px solid ${theme.colors.gray[2]}`,
                        backgroundColor: theme.colors.gray[0]
                    }}>
                        Caricamento altri articoli...
                    </div>
                )}
                
                {!rowLoading && displayedRecords.length >= filteredData.length && filteredData.length > 0 && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '10px', 
                        color: theme.colors.gray[5],
                        borderTop: `1px solid ${theme.colors.gray[2]}`,
                        backgroundColor: theme.colors.gray[0],
                        fontSize: '12px'
                    }}>
                        Visualizzati tutti i {filteredData.length} articoli
                    </div>
                )}
            </div>
        </>
    );
};

export default MarketplaceStatusTable;
